<template>
    <tile class="grow w-full max-w-[1920px] flex flex-col">
        <div class="text-lg text-center">Månedlig inntekt og kostnad</div>
        <div class="flex justify-between">
            <div class="">
                <div class="text-xs">Inntekt</div>
                <div class="text-lg text-primary">{{ formatCurrency(totalIncome, 0) }}</div>
            </div>
            <div class="text-right">
                <div class="text-xs">Kostnader</div>
                <div class="text-lg text-orange-600">{{ formatCurrency(operationalExpenses, 0) }}</div>
            </div>
        </div>
        <ag-charts v-if="profitAndLossReport" :options="options" class="grow"/>
    </tile>
</template>

<script setup lang="ts">
import {AgCharts} from 'ag-charts-vue3'
import Tile from '@/dashboard/Tile.vue'
import {computed} from 'vue'
import {AgChartOptions, AgColorType} from 'ag-charts-enterprise'
import {OPERATING_COSTS, OPERATIONAL_INCOME, OTHER_INCOME, OTHER_OPERATING_COSTS, PERSONNEL_COSTS, ProfLosReport, SALES_INCOME} from '@/proflos/ProfLosReport'
import {isDark} from '@/theme/theme.service'
import {agChartProDark, agChartProLight} from '@/agUtils/agChartsTheme'
import {formatCurrency} from '@/services/formatter.service'
import {$dt} from '@primeuix/themes'
import {format} from 'date-fns'
import {nb} from 'date-fns/locale'
import {capitalize} from 'lodash'

const props = defineProps({
    profitAndLossReport: {
        type: Object as () => ProfLosReport,
        required: false
    }
})

const totalIncome = computed(() => {
    return props.profitAndLossReport?.operationalIncome().amount || 0
})

const operationalExpenses = computed(() => {
    return props.profitAndLossReport?.operationalExpenses().amount || 0
})

const operationalIncome = computed(() => {
    return props.profitAndLossReport?.getGroupByName(OPERATIONAL_INCOME)
})

const salesIncome = computed(() => {
    return props.profitAndLossReport?.getGroupByName(SALES_INCOME)
})

const otherIncome = computed(() => {
    return props.profitAndLossReport?.getGroupByName(OTHER_INCOME)
})

const operatingCosts = computed(() => {
    return props.profitAndLossReport?.getGroupByName(OPERATING_COSTS)
})

const personnelCosts = computed(() => {
    return props.profitAndLossReport?.getGroupByName(PERSONNEL_COSTS)
})

const otherOperatingCosts = computed(() => {
    return props.profitAndLossReport?.getGroupByName(OTHER_OPERATING_COSTS)
})


const monthlyMovements = computed(() => {

    return operationalIncome.value?.monthlyMovements.map((movement, index) => {
        return {
            month: movement.year + '-' +
                //zero pad month to two digits
                (movement.month < 10 ? '0' + movement.month : movement.month),
            salesIncome: (salesIncome.value?.monthlyMovements[index].amount || 0) * -1,
            otherIncome: (otherIncome.value?.monthlyMovements[index].amount || 0) * -1,
            operatingCosts: operatingCosts.value?.monthlyMovements[index].amount || 0,
            personnelCosts: personnelCosts.value?.monthlyMovements[index].amount || 0,
            otherOperatingCosts: otherOperatingCosts.value?.monthlyMovements[index].amount || 0
        }
    })
})



const monthDisplay = (yyyyMm: string): string => {
    return capitalize(format(new Date(yyyyMm), 'MMMM yyyy', {locale: nb}))
}

const tooltip = {
    //TODO: Typing
    renderer: ({title, stackGroup, datum, yKey, fill}: any ) => {
        const total = stackGroup === 'income' ? datum.salesIncome + datum.otherIncome :
            datum.operatingCosts + datum.personnelCosts + datum.otherOperatingCosts
        return `
            <div class="p-2">
                <div>${monthDisplay(datum.month)}</div>
                <div class="flex items-center gap-2 mt-2">
                    <div class="w-3 h-3" style="background-color: ${fill}"></div>
                    <div class="mr-4">${title}</div>
                    <div>${formatCurrency(datum[yKey])}</div>
                </div>
                <div class="flex mt-2 justify-between">
                    <div>Total ${stackGroup === 'income' ? 'inntekt': 'kostnad'}</div>
                    <div>${formatCurrency(total)}</div>
                </div>
            </div>`
    }
}

const options = computed<AgChartOptions>(() => {
    return {
        theme: {
            ...(isDark.value ? agChartProDark : agChartProLight),
            palette: {
                fills: [
                    $dt('green.600').value, $dt('green.400').value as AgColorType[],
                    $dt('orange.600').value, $dt('orange.500').value, $dt('orange.400').value as AgColorType[]
                ]
            }
        },

        data: monthlyMovements.value,
        series: [
            {type: 'bar', xKey: 'month', yKey: 'salesIncome', yName: 'Salgsinntekt', stackGroup: 'income', tooltip},
            {type: 'bar', xKey: 'month', yKey: 'otherIncome', yName: 'Annen driftsinntekt', stackGroup: 'income', tooltip},
            {type: 'bar', xKey: 'month', yKey: 'operatingCosts', yName: 'Varekostnad', stackGroup: 'expense', tooltip},
            {type: 'bar', xKey: 'month', yKey: 'personnelCosts', yName: 'Lønnskostnad', stackGroup: 'expense', tooltip},
            {type: 'bar', xKey: 'month', yKey: 'otherOperatingCosts', yName: 'Annen driftskostnad', stackGroup: 'expense', tooltip}
        ],
        axes: [
            {
                type: 'category',
                position: 'bottom',
                crossLines: [
                    {
                        type: 'line',
                        lineDash: [4, 4],
                        //Value is current month in the form of 'YYYY-MM'
                        value: new Date().toISOString().slice(0, 7) // 'YYYY-MM'
                    }
                ],
                label: {
                    formatter: (params) => {
                        //First parse month from string in the form of 'YYYY-MM', then show it as friendly month name
                        if (!params.value) {
                            return ''
                        }
                        const date = new Date(params.value + '-01') // Append day to make it a valid date
                        if (isNaN(date.getTime())) {
                            console.warn('Invalid date created from month:', params.value)
                            return ''
                        }
                        const options: Intl.DateTimeFormatOptions = {month: 'short'}
                        return date.toLocaleDateString('no-NO', options)
                    }
                }
            },
            {
                type: 'number',
                nice: false,
                position: 'left',
                crosshair: {
                    enabled: false
                },
                line: {
                    enabled: false
                },
                label: {
                    enabled: false
                }
            }
        ],
        minHeight: 100,
        legend: {
            position: 'right',
            enabled: false
        },
        padding: {
            top: 5,
            right: 0,
            bottom: 5,
            left: 0
        },
        background: {
            visible: false
        }
    } as AgChartOptions
})

</script>

<style scoped>

</style>